import { useState, useEffect } from 'react'
import { PageHeader, SearchInput, Modal, EmptyState } from '../../components/UI'
import { get, post, put } from '../../utils/api'
import { formatCurrency, formatDate } from '../../utils/helpers'
import { IconBuildingSkyscraper, IconPlus, IconEdit, IconEye } from '@tabler/icons-react'

const PLANS = ['trial', 'starter', 'professional', 'enterprise']
const STATUSES = ['active', 'trial', 'past_due', 'cancelled', 'suspended']

export default function AdminHotels() {
  const [hotels, setHotels] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [detail, setDetail] = useState(null)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => { try { setHotels(await get('/admin/hotels')) } catch {} finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  const filtered = hotels.filter(h => {
    if (!search) return true
    const s = search.toLowerCase()
    return h.name.toLowerCase().includes(s) || (h.city || '').toLowerCase().includes(s)
  })

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/admin/hotels', formData); setShowAdd(false); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const handleUpdate = async () => {
    setSaving(true)
    try { await put(`/admin/hotels/${editing.id}`, formData); setEditing(null); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const openEdit = (h) => { setEditing(h); setFormData({ ...h }) }

  const viewDetail = async (id) => {
    try { setDetail(await get(`/admin/hotels/${id}`)) } catch {}
  }

  const statusColor = (s) => ({ active: 'green', trial: 'blue', past_due: 'orange', cancelled: 'red', suspended: 'red' }[s] || 'secondary')
  const planColor = (p) => ({ trial: 'secondary', starter: 'blue', professional: 'purple', enterprise: 'green' }[p] || 'secondary')

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Hotels" subtitle={`${hotels.length} hotels on the platform`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => { setFormData({}); setShowAdd(true) }}><IconPlus size={16} className="me-1" />Add Hotel</button>} />

      {/* Stats */}
      <div className="row row-cards mb-3">
        {[{ l: 'Total', v: hotels.length }, { l: 'Active', v: hotels.filter(h => h.subscription_status === 'active').length },
          { l: 'Trial', v: hotels.filter(h => h.plan === 'trial').length },
          { l: 'MRR', v: formatCurrency(hotels.filter(h => h.subscription_status === 'active').reduce((s, h) => s + parseFloat(h.monthly_fee || 0), 0)) }
        ].map(s => (
          <div key={s.l} className="col-6 col-lg-3"><div className="card stat-mini"><div className="card-body">
            <div className="subheader">{s.l}</div><div className="h3 mb-0">{s.v}</div>
          </div></div></div>
        ))}
      </div>

      <div className="d-flex mb-3"><SearchInput value={search} onChange={setSearch} placeholder="Search hotels..." /></div>

      {filtered.length === 0 ? <EmptyState icon={IconBuildingSkyscraper} title="No hotels found" /> : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover">
              <thead><tr><th>Hotel</th><th>Location</th><th>Rooms</th><th>Plan</th><th>Status</th><th>MRR</th><th>Users</th><th>Leads</th><th>Deals</th><th>Actions</th></tr></thead>
              <tbody>{filtered.map(h => (
                <tr key={h.id}>
                  <td className="fw-medium">{h.name}</td>
                  <td>{h.city}, {h.state}</td>
                  <td>{h.total_rooms}</td>
                  <td><span className={`badge bg-${planColor(h.plan)}-lt`}>{h.plan}</span></td>
                  <td><span className={`badge bg-${statusColor(h.subscription_status)}-lt`}>{(h.subscription_status || '').replace(/_/g, ' ')}</span></td>
                  <td className="fw-medium">{formatCurrency(h.monthly_fee)}</td>
                  <td>{h.user_count}</td>
                  <td>{h.lead_count}</td>
                  <td>{h.deal_count}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-primary btn-icon btn-sm" onClick={() => viewDetail(h.id)} title="View"><IconEye size={16} /></button>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={() => openEdit(h)} title="Edit"><IconEdit size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name || 'Hotel'} wide>
        {detail && (
          <div>
            <div className="datagrid mb-4">
              <div className="datagrid-item"><div className="datagrid-title">Location</div><div className="datagrid-content">{detail.city}, {detail.state}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Rooms</div><div className="datagrid-content">{detail.total_rooms}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Plan</div><div className="datagrid-content"><span className={`badge bg-${planColor(detail.plan)}-lt`}>{detail.plan}</span></div></div>
              <div className="datagrid-item"><div className="datagrid-title">Status</div><div className="datagrid-content"><span className={`badge bg-${statusColor(detail.subscription_status)}-lt`}>{detail.subscription_status}</span></div></div>
              <div className="datagrid-item"><div className="datagrid-title">MRR</div><div className="datagrid-content fw-bold">{formatCurrency(detail.monthly_fee)}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Since</div><div className="datagrid-content">{detail.subscription_start ? formatDate(detail.subscription_start) : '---'}</div></div>
            </div>
            <div className="row row-cards mb-3">
              {[{ l: 'Leads', v: detail.stats.leads }, { l: 'Deals', v: detail.stats.deals },
                { l: 'Pipeline', v: formatCurrency(detail.stats.pipeline) }, { l: 'Events', v: detail.stats.events }
              ].map(s => (
                <div key={s.l} className="col-3"><div className="card stat-mini"><div className="card-body text-center">
                  <div className="subheader">{s.l}</div><div className="h3 mb-0">{s.v}</div>
                </div></div></div>
              ))}
            </div>
            <h4 className="mb-2">Users ({detail.users.length})</h4>
            <div className="table-responsive">
              <table className="table table-sm table-vcenter">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Last Login</th></tr></thead>
                <tbody>{detail.users.map(u => (
                  <tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td style={{ textTransform: 'capitalize' }}>{(u.role || '').replace(/_/g, ' ')}</td>
                    <td><span className={`badge bg-${u.is_active ? 'green' : 'red'}-lt`}>{u.is_active ? 'Yes' : 'No'}</span></td>
                    <td>{u.last_login ? formatDate(u.last_login.split('T')[0]) : 'Never'}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Hotel" wide>
        <div className="row g-3">
          {[{ label: 'Hotel Name', key: 'name' }, { label: 'City', key: 'city' }, { label: 'State', key: 'state' },
            { label: 'Total Rooms', key: 'total_rooms', type: 'number' }, { label: 'Tax Rate %', key: 'tax_rate', type: 'number' },
            { label: 'Monthly Fee', key: 'monthly_fee', type: 'number' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-md-6"><label className="form-label">Plan</label>
            <select className="form-select" value={formData.plan || ''} onChange={e => setFormData({ ...formData, plan: e.target.value })}>
              <option value="">Select...</option>{PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}</select></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>{saving ? 'Creating...' : 'Add Hotel'}</button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit: ${editing?.name || ''}`} wide>
        <div className="row g-3">
          {[{ label: 'Hotel Name', key: 'name' }, { label: 'City', key: 'city' }, { label: 'State', key: 'state' },
            { label: 'Total Rooms', key: 'total_rooms', type: 'number' }, { label: 'Tax Rate %', key: 'tax_rate', type: 'number' },
            { label: 'Monthly Fee', key: 'monthly_fee', type: 'number' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-md-6"><label className="form-label">Plan</label>
            <select className="form-select" value={formData.plan || ''} onChange={e => setFormData({ ...formData, plan: e.target.value })}>
              {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label">Subscription Status</label>
            <select className="form-select" value={formData.subscription_status || ''} onChange={e => setFormData({ ...formData, subscription_status: e.target.value })}>
              {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label">Active</label>
            <select className="form-select" value={formData.is_active === false ? 'false' : 'true'} onChange={e => setFormData({ ...formData, is_active: e.target.value === 'true' })}>
              <option value="true">Active</option><option value="false">Inactive</option></select></div>
          <div className="col-12"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} /></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleUpdate}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </Modal>
    </>
  )
}
